import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, FloppyDisk, ArrowsClockwise, ArrowCounterClockwise } from "@phosphor-icons/react";

const MODULE_LABELS = {
  customers: "Customers", suppliers: "Suppliers", products: "Products", services: "Services", stock: "Stock",
  quotations: "Quotations", sales: "Sales Invoices", purchases: "Purchase Invoices",
  returns: "Sales Returns", payments: "Payment Vouchers", expenses: "Expenses",
  targets: "Targets", reminders: "Reminders", reports: "Reports", gst: "GST Reports",
  pending_works: "Daily Pending Works",
  cash_book: "Cash Book", daily_close: "Daily Close",
  dsc: "DSC Register",
  outward_inward: "Outward / Inward",
  credentials: "Credentials Vault",
};
const ACTION_LABELS = {
  view: "View",
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  export: "Export (Print / Excel)",
};

function RoleMatrix({ role, modules, actions, onSaved }) {
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/permissions?role=${role}`);
      setPerms(data.permissions || {});
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  const toggle = (module, action) => {
    const key = `${module}.${action}`;
    setPerms((p) => ({ ...p, [key]: !p[key] }));
  };

  const setAll = (module, value) => {
    const patch = {};
    actions.forEach((a) => { patch[`${module}.${a}`] = value; });
    setPerms((p) => ({ ...p, ...patch }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/permissions/${role}`, { permissions: perms });
      toast.success(`${role === "manager" ? "Manager" : "Employee"} permissions saved`);
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm(`Reset ${role === "manager" ? "Manager" : "Employee"} permissions to defaults? Custom toggles will be lost.`)) return;
    setSaving(true);
    try {
      const { data } = await api.delete(`/permissions/${role}`);
      setPerms(data.permissions || {});
      toast.success("Permissions reset to defaults");
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-muted-foreground p-4">Loading permissions…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-muted-foreground">
          Toggle the actions available to <span className="font-semibold text-foreground">{role === "manager" ? "Managers" : "Employees"}</span> in this company. Admin is always allowed.
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={resetDefaults} className="h-8" data-testid={`perm-reset-${role}`}>
            <ArrowCounterClockwise size={13} className="mr-1.5" /> Reset to Defaults
          </Button>
          <Button variant="outline" size="sm" onClick={load} className="h-8" data-testid={`perm-reload-${role}`}>
            <ArrowsClockwise size={13} className="mr-1.5" /> Reload
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="h-8" data-testid={`perm-save-${role}`}>
            <FloppyDisk size={13} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table" data-testid={`perm-table-${role}`}>
          <thead>
            <tr>
              <th style={{ width: "28%" }}>Module</th>
              {actions.map((a) => <th key={a} className="text-center">{ACTION_LABELS[a] || a}</th>)}
              <th className="text-center" style={{ width: "8%" }}>All</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => {
              const allOn = actions.every((a) => perms[`${m}.${a}`]);
              return (
                <tr key={m}>
                  <td className="font-medium">{MODULE_LABELS[m] || m}</td>
                  {actions.map((a) => {
                    const key = `${m}.${a}`;
                    return (
                      <td key={a} className="text-center">
                        <Switch
                          checked={!!perms[key]}
                          onCheckedChange={() => toggle(m, a)}
                          data-testid={`perm-${role}-${m}-${a}`}
                        />
                      </td>
                    );
                  })}
                  <td className="text-center">
                    <Switch
                      checked={allOn}
                      onCheckedChange={(v) => setAll(m, v)}
                      data-testid={`perm-${role}-${m}-all`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Permissions() {
  const { activeCompanyId, reloadPermissions } = useAuth();
  const [meta, setMeta] = useState({ modules: [], actions: [] });

  useEffect(() => {
    api.get("/permissions/modules").then((r) => setMeta(r.data));
  }, []);

  return (
    <div className="page-shell space-y-6">
      <div>
        <div className="eyebrow flex items-center gap-1.5">
          <ShieldCheck size={11} /> Administration
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Permissions</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Fine-tune what Managers and Employees can do inside the active company. Admin always retains full control.
        </p>
      </div>

      <Tabs defaultValue="manager">
        <TabsList data-testid="perm-tabs">
          <TabsTrigger value="manager" data-testid="perm-tab-manager">Manager</TabsTrigger>
          <TabsTrigger value="employee" data-testid="perm-tab-employee">Employee</TabsTrigger>
        </TabsList>
        <TabsContent value="manager" className="mt-4">
          {meta.modules.length > 0 && (
            <RoleMatrix role="manager" modules={meta.modules} actions={meta.actions} onSaved={reloadPermissions} />
          )}
        </TabsContent>
        <TabsContent value="employee" className="mt-4">
          {meta.modules.length > 0 && (
            <RoleMatrix role="employee" modules={meta.modules} actions={meta.actions} onSaved={reloadPermissions} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
